/**
 * Pe un magazin, textul liber al capturii e în cea mai mare parte redundant:
 * titlurile, prețurile și variantele ajung deja la model prin oglinda de catalog,
 * iar o pagină de colecție repetă în plus, pentru fiecare card, același balast de
 * temă („Quick view", „Add to cart", vendorul, steluțele, „In stock"). Măsurat pe
 * o grilă Warehouse de 12 produse: peste 2.000 de caractere care nu spun nimic ce
 * modelul să nu știe deja.
 *
 * Ce NU se poate afla din catalog e exact ce rămâne aici: CARE produse sunt pe
 * ecran ACUM și în ce ordine - referentul pentru „al doilea". Deci nu ștergem
 * cardurile, le comprimăm: fiecare card devine o linie (titlu - preț - cale),
 * într-un bloc delimitat, la locul primului card din pagină. Restul paginii
 * (antet, filtre, paginare, bannere) rămâne neatins.
 *
 * Nu atinge nici lista de elemente pointabile, nici harta `idx → Element`:
 * lucrează exclusiv pe CLONA din care se citește textul (`visibleText`), după ce
 * aceasta a fost deja curățată de zonele excluse de proprietar și golită de
 * câmpurile purtătoare de valoare. Citirea textului vine injectată (`textOf`),
 * ca disciplina „nu capturăm valori din formulare" să rămână într-un singur loc.
 */

/** Linkurile de produs Shopify - singurul contract comun tuturor temelor. */
const PRODUCT_LINK_SELECTOR = "a[href*='/products/']";

/**
 * Containere care grupează MAI MULTE carduri: urcarea spre rădăcina cardului se
 * oprește sub ele. Fără lista asta, o colecție cu un singur produs ar înghiți
 * antetul și paginarea în „cardul" ei.
 */
const CARD_STOP_TAGS = new Set(["MAIN", "SECTION", "NAV", "HEADER", "FOOTER", "FORM", "UL", "OL", "TABLE", "BODY"]);

/** Cât de sus urcăm de la link spre rădăcina cardului - o temă nu îl îngroapă mai adânc. */
const MAX_CARD_DEPTH = 6;

const MAX_TITLE_CHARS = 80;

/**
 * Un token care arată a preț: simbol înaintea cifrelor („$12.34", „€1.234,56")
 * sau monedă după ele („1.219,00 lei"). Deliberat îngust - „Save 19%" și
 * „4.5 (127 reviews)" nu sunt prețuri. Luăm PRIMUL token din card care NU e
 * o economie: temele randează prețul curent înaintea celui tăiat, deci primul
 * e cel care se plătește - cu o excepție care a costat două prețuri greșite
 * rostite vizitatorului (staging, 12 și 21 aug 2026): badge-ul „Economisești
 * 64 lei" al temei Warehouse stă ÎNAINTEA prețului, în zona imaginii. Agentul a
 * spus „costă doar șaizeci și patru de lei" pentru un trimmer de 1.219 (1.283 −
 * 1.219 = 64) și „o mie o sută nouă lei" pentru o mașină de 2.790. De-aia
 * fiecare token se citește împreună cu cuvintele dinaintea lui.
 */
const PRICE_RE = /[$£€]\s?\d[\d.,]*|\d[\d.,]*\s?(?:lei|ron|eur|usd)\b/gi;

/**
 * Cuvintele care, înaintea unei sume, o fac economie și nu preț: RO/EN, cu și
 * fără diacritice („Economisești", „Economisesti", „Save 64 lei", „Reducere
 * 200 lei", „-200 lei"). Fereastra e scurtă (24 de caractere) ca să nu atingă
 * prețul curent care urmează imediat după badge.
 */
const SAVING_CONTEXT_RE = /(?:(?:econom|\bsav(?:e|ing|ings)\b|you save|reduc|discount|rabat|sconto|spar)[^\d]{0,16}|-\s*)$/i;
const SAVING_WINDOW_CHARS = 24;

export interface ProductLine {
  title: string;
  price?: string;
  path: string;
}

/**
 * Calea produsului, fără origine și fără query: `?variant=` diferă între linkul
 * imaginii și cel al titlului, deși e același card.
 */
function productPath(href: string): string | null {
  if (!href) return null;
  let pathname: string;
  try {
    pathname = new URL(href, location.href).pathname;
  } catch {
    pathname = href.split(/[?#]/)[0];
  }
  return /\/products\/[^/]+/.test(pathname) ? pathname : null;
}

/** Exportat pentru teste; singurul consumator real e `readProductCards`. */
export function priceIn(text: string): string | undefined {
  for (const match of text.matchAll(PRICE_RE)) {
    const at = match.index ?? 0;
    const before = text.slice(Math.max(0, at - SAVING_WINDOW_CHARS), at);
    if (SAVING_CONTEXT_RE.test(before)) continue;
    return match[0].replace(/[.,]$/, "").trim();
  }
  return undefined;
}

function hasOtherProduct(el: Element, path: string): boolean {
  for (const link of el.querySelectorAll(PRODUCT_LINK_SELECTOR)) {
    const other = productPath(link.getAttribute("href") ?? "");
    if (other && other !== path) return true;
  }
  return false;
}

/**
 * Rădăcina cardului: cel mai de sus strămoș care încă vorbește despre UN SINGUR
 * produs. Euristica e deliberat structurală (linkuri + containere de grilă), nu
 * pe clase de temă - Warehouse, Dawn și derivatele lor n-au nimic comun în
 * numele claselor.
 */
function cardRootFor(anchor: Element, path: string, root: Element): Element {
  let node = anchor;
  for (let depth = 0; depth < MAX_CARD_DEPTH; depth++) {
    const parent = node.parentElement;
    if (!parent || parent === root || CARD_STOP_TAGS.has(parent.tagName)) break;
    if (hasOtherProduct(parent, path)) break;
    node = parent;
  }
  return node;
}

/**
 * Un link de produs dintr-un paragraf de proză nu e un card, iar comprimarea lui
 * ar șterge fraza din jur. Cerem semnul minim al unui card: o imagine sau un preț.
 */
function looksLikeCard(card: Element, text: string): boolean {
  return card.querySelector("img") !== null || priceIn(text) !== undefined;
}

/**
 * Textul unui link de produs care NU e un titlu: badge-ul de economie din zona
 * imaginii („Economisești 64 lei" - Warehouse îl pune în același `<a>` cu
 * imaginea, primul link din card) sau un preț singur. Fără filtrul ăsta, linia
 * cardului devenea „1. Economisești 64 lei - 1.219,00 lei - /products/…" și
 * agentul citea economia ca nume de produs.
 */
function looksLikeTitle(text: string): boolean {
  const t = text.trim();
  if (t === "") return false;
  if (/^(?:econom|you save|sav(?:e|ing|ings)\b|reduc|discount|rabat|sconto|spar)/i.test(t)) return false;
  return t.replace(PRICE_RE, "").trim() !== "";
}

function titleOf(card: Element, anchor: Element, path: string, textOf: (el: Element) => string): string {
  const heading = card.querySelector("h1,h2,h3,h4,h5,h6");
  const named = [...card.querySelectorAll(PRODUCT_LINK_SELECTOR)].map(textOf).find(looksLikeTitle);
  const raw =
    (heading ? textOf(heading) : "") ||
    (looksLikeTitle(textOf(anchor)) ? textOf(anchor) : "") ||
    named ||
    card.querySelector("img")?.getAttribute("alt") ||
    decodeURIComponent(path.split("/").pop() ?? "");
  return raw.replace(/\s+/g, " ").trim().slice(0, MAX_TITLE_CHARS);
}

/**
 * Cardurile de produs din `root`, în ordinea documentului, deduplicate pe cale.
 * Exportat pentru teste; `collapseProductCards` e singurul consumator real.
 */
export function readProductCards(
  root: Element,
  textOf: (el: Element) => string,
): { lines: ProductLine[]; cards: Element[] } {
  const lines: ProductLine[] = [];
  const cards: Element[] = [];
  const seen = new Set<string>();
  for (const anchor of root.querySelectorAll(PRODUCT_LINK_SELECTOR)) {
    if (cards.some((card) => card.contains(anchor))) continue;
    const path = productPath(anchor.getAttribute("href") ?? "");
    if (!path) continue;
    const card = cardRootFor(anchor, path, root);
    const text = textOf(card);
    if (!looksLikeCard(card, text)) continue;
    cards.push(card);
    // Un al doilea card spre același produs (secțiunea „recomandate", grila
    // duplicată de temă) se comprimă la fel, dar nu mai adaugă o linie: lista e
    // răspunsul la „care produse sunt pe ecran", nu la „de câte ori apar".
    if (seen.has(path)) continue;
    seen.add(path);
    const price = priceIn(text);
    lines.push({ title: titleOf(card, anchor, path, textOf), ...(price ? { price } : {}), path });
  }
  return { lines, cards };
}

function formatBlock(lines: ProductLine[]): string {
  const items = lines.map(
    (line, i) => `${i + 1}. ${line.title}${line.price ? ` - ${line.price}` : ""} - ${line.path}`,
  );
  return `[Products visible on screen, in order: ${items.join("; ")}]`;
}

/**
 * Înlocuiește, ÎN CLONĂ, textul cardurilor de produs cu un singur bloc
 * structurat, pus exact acolo unde începea primul card (deci ordinea față de
 * restul paginii se păstrează). Fără carduri recunoscute nu schimbă nimic.
 */
export function collapseProductCards(root: HTMLElement, textOf: (el: Element) => string): void {
  const { lines, cards } = readProductCards(root, textOf);
  if (lines.length === 0) return;
  const block = root.ownerDocument.createElement("div");
  block.textContent = formatBlock(lines);
  cards[0].parentNode?.insertBefore(block, cards[0]);
  for (const card of cards) card.remove();
}
